import React, { FC } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { Form, FormDrawer } from '~/components';
import { usePermissions } from '~/hooks';
import { useBeneficiaryAccountsEdit } from '~/hooks/beneficiaryAccounts';
import { Beneficiary, BeneficiaryAccount } from '~/types';
import AccountFormItems from './AccountFormItems';

interface Props {
  open: boolean;
  onClose: () => void;
  beneficiary: Beneficiary;
  beneficiaryAccount: BeneficiaryAccount;
}

const EditAccount: FC<Props> = (props) => {
  const { open, onClose, beneficiary, beneficiaryAccount: initialBeneficiaryAccount } = props;

  const beneficiaryEditMutation = useBeneficiaryAccountsEdit();

  const createAccount = async (account: BeneficiaryAccount) => {
    // const result = { ...beneficiary };
    const p = account.parameters;
    let newName = account.serviceName;
    if (p.accountNumber) {
      newName = `${newName} ${p.accountNumber.value}`;
    } else if (p.iban) {
      newName = `${newName} ${p.iban.value}`;
    }

    const result = {
      ...account,
      name: newName,
      solutionId: beneficiary.solutionId,
      profileId: beneficiary.profileId,
      beneficiaryId: beneficiary.id,
      id: initialBeneficiaryAccount.id,
    };
    await beneficiaryEditMutation.mutateAsync({
      beneficiaryId: beneficiary.id,
      id: initialBeneficiaryAccount.id,
      body: result,
    });
    onClose();
  };

  const onFinish = (account: BeneficiaryAccount) => {
    const { setAsPrimary } = account;
    if (setAsPrimary && !initialBeneficiaryAccount.primary) {
      Modal.confirm({
        title: t('sureAddOrEdit'),
        content: t('editPrimaryAccount'),
        icon: <ExclamationCircleOutlined />,
        okText: dt('yes'),
        okType: 'danger',
        cancelText: dt('no'),
        onOk() {
          void createAccount(account);
        },
      });
    } else void createAccount(account);
  };

  const [form] = Form.useForm();

  const [t] = useTranslation('payouts');
  const dt = (key: string): string => t(`translation:${key}`);
  const hasAccessUpdate = usePermissions('business.beneficiary.update');
  if (!hasAccessUpdate) return null;
  return (
    <FormDrawer
      name={name}
      onFinish={onFinish}
      title={t('editAccount')}
      onClose={onClose}
      visible={open}
      loading={beneficiaryEditMutation.isLoading}
    >
      <Form layout="vertical" form={form} name={name} initialValues={initialBeneficiaryAccount}>
        <AccountFormItems form={form} />
      </Form>
    </FormDrawer>
  );
};

const name = 'nominatedAccountEdit';

export default EditAccount;
